import React from 'react';
import { Card } from './ui/Card';
import { cn, formatNumber } from '../lib/utils';
import { LucideIcon } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: LucideIcon;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  accentColor?: 'orange' | 'emerald' | 'sky' | 'indigo' | 'amber';
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  accentColor = 'orange',
}) => {
  const accentStyles = {
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    sky: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
    indigo: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  };

  const formattedValue = typeof value === 'number' ? formatNumber(value) : value;

  return (
    <Card hoverEffect className="relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{title}</p>
          <h4 className="text-2xl font-extrabold text-slate-50 mt-1.5 tracking-tight font-mono">
            {formattedValue}
          </h4>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          {trend && (
            <div className="flex items-center gap-1.5 mt-2 text-xs">
              <span
                className={cn(
                  'font-semibold',
                  trend.isPositive ? 'text-emerald-400' : 'text-rose-400'
                )}
              >
                {trend.value}
              </span>
              <span className="text-slate-500">vs previous period</span>
            </div>
          )}
        </div>
        <div className={cn('p-2.5 rounded-xl border', accentStyles[accentColor])}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
};
